import { motion } from 'framer-motion';
import { BookOpen, Brain, Lightbulb, Search, Terminal, Users, ArrowRight, Clock, Star, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { PageLayout } from '@/components/layout/PageLayout';

const Learn = () => {
  const featuredModules = [
    {
      id: 1,
      title: "AI Fundamentals for Executives",
      description: "Core AI concepts every C-level executive needs to understand.",
      category: "Basics",
      progress: 80,
      duration: "45 min",
      level: "Beginner",
      icon: <Brain className="h-10 w-10" />
    },
    {
      id: 2,
      title: "Strategic AI Implementation",
      description: "How to effectively integrate AI into your organizational strategy.",
      category: "Strategy",
      progress: 30,
      duration: "60 min",
      level: "Intermediate",
      icon: <Lightbulb className="h-10 w-10" />
    },
    {
      id: 3,
      title: "Prompt Engineering Mastery",
      description: "Advanced techniques to get the most out of AI language models.",
      category: "Technical",
      progress: 0,
      duration: "40 min",
      level: "Advanced",
      icon: <Terminal className="h-10 w-10" />
    }
  ];
  
  const allModules = [
    ...featuredModules,
    {
      id: 4,
      title: "AI Ethics for Leaders",
      description: "Navigate ethical considerations and responsible AI adoption.",
      category: "Leadership",
      progress: 0,
      duration: "30 min",
      level: "Intermediate",
      icon: <Users className="h-10 w-10" />
    },
    {
      id: 5,
      title: "AI Tools Comparison",
      description: "A deep dive into leading AI platforms and their business applications.",
      category: "Tools",
      progress: 0,
      duration: "35 min",
      level: "Beginner",
      icon: <BookOpen className="h-10 w-10" />
    }
  ];

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold font-lexend">Learning Center</h1>
            <p className="text-muted-foreground">Expand your AI knowledge with our curated content</p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input 
              placeholder="Search modules..." 
              className="pl-10" 
            />
          </div>
        </div>

        <Card className="glass-card mb-8 border-primary/20 bg-primary/5">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-1">Learning Paths</h2>
                  <p className="text-muted-foreground">
                    Follow structured learning journeys to master AI in business
                  </p>
                </div>
              </div>
              <Button asChild>
                <Link to="/learning-paths">
                  Browse Learning Paths
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="featured" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-8">
            <TabsTrigger value="featured">Featured</TabsTrigger>
            <TabsTrigger value="all">All Modules</TabsTrigger>
            <TabsTrigger value="saved">Saved</TabsTrigger>
          </TabsList>
          
          <TabsContent value="featured" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredModules.map((module) => (
                <LearningModuleCard key={module.id} module={module} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="all" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allModules.map((module) => (
                <LearningModuleCard key={module.id} module={module} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="saved" className="space-y-8">
            <div className="text-center py-8">
              <BookOpen className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-medium font-lexend mb-2">No saved modules yet</h3>
              <p className="text-muted-foreground mb-4">
                Save modules to access them quickly later
              </p>
              <Button variant="outline" asChild>
                <Link to="/learn?tab=all">
                  Browse All Modules
                </Link>
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

interface LearningModule {
  id: number;
  title: string;
  description: string;
  category: string;
  progress: number;
  duration: string;
  level: string;
  icon: React.ReactNode;
}

const LearningModuleCard = ({ module }: { module: LearningModule }) => {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card className="glass-card overflow-hidden h-full flex flex-col">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start mb-2">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              {module.icon}
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-xs font-medium bg-secondary/70 px-2 py-1 rounded">
                {module.level}
              </span>
            </div>
          </div>
          <CardTitle className="text-xl font-lexend line-clamp-1">{module.title}</CardTitle>
          <CardDescription className="line-clamp-2">{module.description}</CardDescription>
        </CardHeader>
        <CardContent className="pb-4 flex-grow">
          <div className="flex items-center justify-between mb-2 text-sm">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
              <span className="text-muted-foreground">{module.duration}</span>
            </div>
            <div className="flex items-center">
              <Star className="h-4 w-4 mr-1 text-muted-foreground" />
              <span className="text-muted-foreground">{module.category}</span>
            </div>
          </div>
          
          {module.progress > 0 && (
            <div className="mb-1">
              <div className="flex justify-between text-xs mb-1">
                <span>Progress</span>
                <span>{module.progress}%</span>
              </div>
              <Progress value={module.progress} className="h-1" />
            </div>
          )}
        </CardContent>
        <CardFooter className="pt-0">
          <Button asChild className="w-full">
            <Link to={`/learn/${module.id}`}>
              {module.progress > 0 ? "Continue Learning" : "Start Learning"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default Learn;
